import React from 'react';
import { Droplet, Scissors, Beaker } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';

export const ProductsSection = ({ t }) => {
  return (
    <section id="products" className="py-24 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Línea de Productos
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Soluciones farmacéuticas innovadoras respaldadas por investigación científica
          </p>
        </div>

        {/* Products Tabs */}
        <Tabs defaultValue="fillers" className="w-full">
          <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-3 h-14 mb-12 bg-slate-100">
            <TabsTrigger value="fillers" className="text-base data-[state=active]:bg-slate-800 data-[state=active]:text-white">
              <Droplet className="w-4 h-4 mr-2" />
              Hydracore
            </TabsTrigger>
            <TabsTrigger value="plla" className="text-base data-[state=active]:bg-slate-800 data-[state=active]:text-white">
              <Beaker className="w-4 h-4 mr-2" />
              PLLA
            </TabsTrigger>
            <TabsTrigger value="threads" className="text-base data-[state=active]:bg-slate-800 data-[state=active]:text-white">
              <Scissors className="w-4 h-4 mr-2" />
              Hilos
            </TabsTrigger>
          </TabsList>

          {/* Fillers Content */}
          <TabsContent value="fillers" className="mt-8">
            <div className="mb-8 text-center">
              <img src="https://customer-assets.emergentagent.com/job_67b366a2-9c46-4ca8-9e2f-e7e30db89ded/artifacts/gv092awr_Boton%20secci%C3%B3n%20Sfx%20Academy%20%281%29.png" alt="HydraCore" className="h-24 w-auto mx-auto mb-3" />
              <p className="text-lg text-slate-600">{t.products.fillers.description}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {t.products.fillers.items.map((item, index) => (
                <Card 
                  key={index} 
                  data-testid={`hydracore-product-${index}`}
                  className="group relative overflow-hidden hover:shadow-xl transition-all duration-500 border-2 hover:scale-105"
                  style={{ borderColor: item.color }}
                >
                  {/* Product Image */}
                  {item.image && (
                    <div className="flex items-center justify-center p-6 h-64 overflow-hidden relative">
                      <img src="https://customer-assets.emergentagent.com/job_67b366a2-9c46-4ca8-9e2f-e7e30db89ded/artifacts/sb4uob6v_Dise%C3%B1o%20sin%20t%C3%ADtulo%20%2821%29.png" alt="" className="absolute inset-0 w-full h-full object-cover" />
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="relative h-full w-auto object-contain group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <CardHeader className="pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <CardTitle className="text-xl text-slate-900">{item.name}</CardTitle>
                      <Badge 
                        className="text-white border-0 shadow-sm"
                        style={{ backgroundColor: item.color }}
                      >
                        {item.volume}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base text-slate-600">{item.description}</CardDescription>
                  </CardContent>
                  
                  {/* Colored accent bar */}
                  <div 
                    className="absolute bottom-0 left-0 right-0 h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"
                    style={{ backgroundColor: item.color }}
                  ></div>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* PLLA Content */}
          <TabsContent value="plla" className="mt-8">
            <div className="mb-8 text-center">
              <img src="https://customer-assets.emergentagent.com/job_67b366a2-9c46-4ca8-9e2f-e7e30db89ded/artifacts/yljvjln7_Luvenix%20PNG.png" alt="Luvenix" className="h-24 w-auto mx-auto mb-3 rounded-lg" />
              <p className="text-lg text-slate-600">{t.products.plla.description}</p>
            </div>
            {t.products.plla.items.map((item, index) => (
              <Card 
                key={index}
                data-testid={`plla-product-${index}`}
                className="group relative overflow-hidden hover:shadow-2xl transition-all duration-500 border-2 max-w-4xl mx-auto"
                style={{ borderColor: item.color }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2">
                  {/* Images */}
                  <div className="flex items-center justify-center p-8 gap-6 min-h-[320px] relative">
                    <img src="https://customer-assets.emergentagent.com/job_67b366a2-9c46-4ca8-9e2f-e7e30db89ded/artifacts/sb4uob6v_Dise%C3%B1o%20sin%20t%C3%ADtulo%20%2821%29.png" alt="" className="absolute inset-0 w-full h-full object-cover" />
                    {item.imageVial && (
                      <img 
                        src={item.imageVial} 
                        alt={`${item.name} vial`}
                        className="relative h-56 w-auto object-contain group-hover:scale-110 transition-transform duration-500"
                      />
                    )}
                    {item.image && (
                      <img 
                        src={item.image} 
                        alt={`${item.name} caja`}
                        className="relative h-56 w-auto object-contain group-hover:scale-110 transition-transform duration-500"
                      />
                    )}
                  </div>
                  {/* Info */}
                  <div className="p-8 flex flex-col justify-center">
                    <Badge 
                      className="text-white border-0 shadow-sm w-fit mb-4"
                      style={{ backgroundColor: item.color }}
                    >
                      {item.volume}
                    </Badge>
                    <CardTitle className="text-3xl text-slate-900 mb-4">{item.name}</CardTitle>
                    <CardDescription className="text-base text-slate-600 leading-relaxed">{item.description}</CardDescription>
                  </div>
                </div>
                {/* Colored accent bar */}
                <div 
                  className="absolute bottom-0 left-0 right-0 h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"
                  style={{ backgroundColor: item.color }}
                ></div>
              </Card>
            ))}
          </TabsContent>

          {/* Threads Content */}
          <TabsContent value="threads" className="mt-8">
            <div className="mb-8 text-center">
              <h3 className="text-3xl font-bold text-slate-900 mb-3">{t.products.threads.title}</h3>
              <p className="text-lg text-slate-600">{t.products.threads.description}</p>
            </div>

            {/* Helper to render a thread product card */}
            {[
              { line: t.products.threads.stayDensity, id: 'stay-density' },
              { line: t.products.threads.pullFace, id: 'pull-face' }
            ].map(({ line, id }) => (
              <div key={id} className="mb-16 last:mb-0">
                <h4 className="text-2xl font-bold text-slate-800 mb-6 border-l-4 border-slate-800 pl-4">{line.title}</h4>
                <div className={`grid grid-cols-1 ${line.items.length > 2 ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-8`}>
                  {line.items.map((item, index) => (
                    <Card 
                      key={index} 
                      data-testid={`${id}-product-${index}`}
                      className="group relative overflow-hidden hover:shadow-2xl transition-all duration-500 border-2 border-slate-200 hover:border-slate-700"
                    >
                      {/* Product Images */}
                      <div className="flex items-center justify-center p-6 min-h-[240px] gap-4 relative">
                        <img src="https://customer-assets.emergentagent.com/job_67b366a2-9c46-4ca8-9e2f-e7e30db89ded/artifacts/sb4uob6v_Dise%C3%B1o%20sin%20t%C3%ADtulo%20%2821%29.png" alt="" className="absolute inset-0 w-full h-full object-cover" />
                        {item.image && (
                          <img 
                            src={item.image} 
                            alt={`${item.name} packaging`}
                            className="relative h-44 w-auto object-contain group-hover:scale-105 transition-transform duration-500"
                          />
                        )}
                        {item.imageThread && (
                          <img 
                            src={item.imageThread} 
                            alt={`${item.name} thread`}
                            className="relative h-28 w-auto object-contain group-hover:scale-105 transition-transform duration-500"
                          />
                        )}
                      </div>
                      <CardHeader className="pt-4 pb-2">
                        <CardTitle className="text-lg text-slate-900">{item.name}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <CardDescription className="text-sm text-slate-600 leading-relaxed">{item.description}</CardDescription>
                        {item.specs && (
                          <div className="grid grid-cols-2 gap-2 pt-2">
                            {Object.entries(item.specs).map(([key, value]) => (
                              <div key={key} className="bg-slate-50 rounded-md px-3 py-2">
                                <p className="text-xs text-slate-500 capitalize">{key === 'reabsorption' ? 'Reabsorción' : key === 'presentation' ? 'Presentación' : key === 'size' ? 'Medida' : key === 'material' ? 'Material' : key === 'sterilization' ? 'Esterilización' : key === 'content' ? 'Contenido' : key}</p>
                                <p className="text-sm font-medium text-slate-800">{value}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
                {/* Comparison Chart (only for Stay Density) */}
                {line.comparisonChart && (
                  <div className="mt-10">
                    <h5 className="text-xl font-bold text-slate-900 text-center mb-6">Cuadro Comparativo</h5>
                    <div className="max-w-4xl mx-auto rounded-xl overflow-hidden shadow-lg border border-slate-200">
                      <img 
                        src={line.comparisonChart} 
                        alt="Cuadro comparativo"
                        className="w-full h-auto"
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};